import { useMemo } from 'react';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { selectableWorkflowsOf } from '../../../utils/selectable-workflows.util';

import { FormVariant } from '../../enums/variant.enum';
import { FolderBusinessEntityAddressFormStep } from '../../FolderBusinessEntity/steps/Address';
import { FolderBusinessEntityCodingConfigFormStep } from '../../FolderBusinessEntity/steps/CodingConfig';
import { FolderBusinessEntityCodingConfigSchema } from '../../FolderBusinessEntity/steps/CodingConfig/schemas/coding-config.schema';
import { FolderBusinessEntityGeneralInfoFormStep } from '../../FolderBusinessEntity/steps/GeneralInfo';
import { FolderBusinessEntityType } from '../../FolderBusinessEntity/enums/folder-business-entity-type.enum';

import { FolderFormTab } from '../enums/tab.enum';
import { FolderActorsFormStep } from '../steps/Actors';

import { useCodeSuggestion } from './useCodeSuggestion.hook';

interface Params {
  readonly formVariant: FormVariant;
  readonly stepIdsSubset?: readonly FolderFormTab[];
}

export const useFolderSteps = ({
  formVariant,
  stepIdsSubset,
}: Readonly<Params>) => {
  const { folderWorkflows } = useReferencesContext();

  const { steps, wizardChildren } = useMemo(() => {
    const folderGeneralInfo = {
      step: {
        id: FolderFormTab.GeneralInfo,
        title: 'Informations Générales',
        isEnabled: true,
      },
      child: (
        <FolderBusinessEntityGeneralInfoFormStep
          id={FolderFormTab.GeneralInfo}
          key={FolderFormTab.GeneralInfo}
          type={FolderBusinessEntityType.Folder}
          workflows={selectableWorkflowsOf(folderWorkflows)}
        />
      ),
    };

    const folderAddress = {
      step: {
        id: FolderFormTab.Address,
        title: 'Adresse',
        isEnabled: true,
      },
      child: (
        <FolderBusinessEntityAddressFormStep
          id={FolderFormTab.Address}
          key={FolderFormTab.Address}
        />
      ),
    };

    const folderInternalActors = {
      step: {
        id: FolderFormTab.InternalActors,
        title: 'Acteurs SOCNA',
        isEnabled: true,
      },
      child: (
        <FolderActorsFormStep
          id={FolderFormTab.InternalActors}
          isInternal
          key={FolderFormTab.InternalActors}
          useDirectMutation={formVariant === FormVariant.Modification}
        />
      ),
    };

    const folderExternalActors = {
      step: {
        id: FolderFormTab.ExternalActors,
        title: 'Acteurs Externes',
        isEnabled: true,
      },
      child: (
        <FolderActorsFormStep
          id={FolderFormTab.ExternalActors}
          isInternal={false}
          key={FolderFormTab.ExternalActors}
          useDirectMutation={formVariant === FormVariant.Modification}
        />
      ),
    };

    const folderCodingConfig = {
      step: {
        id: FolderFormTab.CodingConfig,
        title: 'Codification',
        isEnabled: true,
        ValidationSchema: FolderBusinessEntityCodingConfigSchema,
      },
      child: (
        <FolderBusinessEntityCodingConfigFormStep
          id={FolderFormTab.CodingConfig}
          key={FolderFormTab.CodingConfig}
          useCodeSuggestion={useCodeSuggestion}
        />
      ),
    };

    const allTabs = [
      [FolderFormTab.GeneralInfo, folderGeneralInfo],
      [FolderFormTab.Address, folderAddress],
      [FolderFormTab.InternalActors, folderInternalActors],
      [FolderFormTab.ExternalActors, folderExternalActors],
      [FolderFormTab.CodingConfig, folderCodingConfig],
    ] as const;

    const activeTabs = stepIdsSubset?.length
      ? allTabs.filter(([tab]) => stepIdsSubset.includes(tab))
      : allTabs;

    const steps = activeTabs.map(([, { step }]) => step);
    const wizardChildren = activeTabs.map(([, { child }]) => child);

    return { steps, wizardChildren };
  }, [stepIdsSubset, folderWorkflows, formVariant]);

  return { steps, wizardChildren };
};
